import classnames from 'classnames';
import {
    COLORS,
    TEXT_COLORS,
    FILLS
} from '../constants';
import {
    BASE_CLASSES,
    MONOCHROME_BUTTONS,
    COLOR_MAP,
    GHOST_COLOR_WEIGHT_MAP,
    ICON_COLOR_WEIGHT_MAP,
    BACKGROUND_COLOR_MAP,
    BACKGROUND_COLOR_WEIGHT_MAP
} from './constants';

/**
 * Calculates the button's appropriate disabled text color weight.
 * @param {Boolean} options.isGhost Whether the button is a ghost button or not.
 * @param {Boolean} options.isMonochromaticButton Whether the button is a monochromatic button (grey, black, etc.) or not.
 * @return {Number}
 */
const disabledColorWeight = ({
    isGhost,
    isMonochromaticButton
}) => {
    if (isGhost) {
        return isMonochromaticButton ? 500 : 300;
    }

    if (isMonochromaticButton) {
        return 600;
    }

    return 100;
};

/**
 * Calculates the button's appropriate text color.
 * @param {String} options.color The button's color.
 * @param {String} options.textColor The button's text color.
 * @param {Boolean} options.disabled Whether the button is disabled or not.
 * @param {Boolean} options.isGhost Whether the button is a ghost button or not.
 * @param {Boolean} options.isWhite Whether the button is a white button or not.
 * @param {Boolean} options.isMonochromaticButton Whether the button is a monochromatic button (grey, black, etc.) or not.
 * @return {String}
 */
const buttonTextColor = ({
    color,
    textColor,
    disabled,
    isGhost,
    isWhite,
    isMonochromaticButton
}) => {
    const baseColor = COLOR_MAP[color] || color;
    const baseTextColor = COLOR_MAP[textColor] || textColor;
    const ghostColorWeight = GHOST_COLOR_WEIGHT_MAP[color] || 700;

    if (disabled) {
        return `${baseColor}-${disabledColorWeight({
            isMonochromaticButton,
            isGhost
        })}`;
    }

    if (isWhite && !isGhost) {
        return `${baseTextColor}-${textColor === TEXT_COLORS.DARK_GREY ? 1000 : 700}`;
    }

    if (!isWhite && isGhost) {
        return `${baseColor}-${ghostColorWeight}`;
    }

    if (color === COLORS.LIGHT_GREY) {
        return `${COLORS.GREY}-1100`;
    }

    return COLORS.WHITE;
};

/**
 * Calculates the button's appropriate background color weight.
 * @param {String} options.color The button's color.
 * @param {Boolean} options.disabled Whether the button is disabled or not.
 * @param {Boolean} options.isMonochromaticButton Whether the button is a monochromatic button (grey, black, etc.) or not.
 * @return {Number}
 */
const backgroundColorWeight = ({
    color,
    disabled,
    isMonochromaticButton
}) => {
    if (disabled) {
        return isMonochromaticButton ? 200 : 300;
    }

    return BACKGROUND_COLOR_WEIGHT_MAP[color] || 700;
};

/**
 * Calculates the button's appropriate background color.
 * @param {String} options.color The button's color.
 * @param {Boolean} options.disabled Whether the button is disabled or not.
 * @param {Boolean} options.isWhite Whether the button is a white button or not.
 * @param {Boolean} options.isMonochromaticButton Whether the button is a monochromatic button (grey, black, etc.) or not.
 * @return {String}
 */
const backgroundColor = ({
    color,
    disabled,
    isWhite,
    isMonochromaticButton
}) => {
    const baseColor = BACKGROUND_COLOR_MAP[color] || color;
    const weight = backgroundColorWeight({
        color,
        disabled,
        isMonochromaticButton
    });

    if (isWhite) {
        return COLORS.WHITE;
    }

    if (!disabled && color === COLORS.BLACK) {
        return COLORS.BLACK;
    }

    return `${baseColor}-${weight}`;
};

/**
 * Calculates the appropriate icon color.
 * @param {String} options.color The button's color.
 * @param {Boolean} options.isGhost Whether the button is a ghost button or not.
 * @return {String}
 */
const iconColor = ({
    color,
    isGhost
}) => {
    const baseColor = COLOR_MAP[color] || color;
    const baseColorWeight = ICON_COLOR_WEIGHT_MAP[color] || 700;

    if (color === COLORS.LIGHT_GREY) {
        return `${COLORS.GREY}-1000`;
    }

    if (isGhost) {
        return `${baseColor}-${baseColorWeight}`;
    }

    return COLORS.WHITE;
};

/**
 * Generates the component's CSS classes.
 * @param {String} options.color The button's color.
 * @param {String} options.fill The button's fill.
 * @param {String} options.size The button's size.
 * @param {Boolean} options.disabled Whether the button is disabled or not.
 * @param {Boolean} options.loading Whether the button is loading or not.
 * @param {String} options.textColor The button's text color.
 * @param {String} [options.className] The button's user-defined classes.
 * @return {String}
 */
const generateClasses = ({
    color,
    fill,
    size,
    disabled,
    loading,
    textColor,
    className
}) => {
    const isGhost = fill === FILLS.GHOST;
    const isWhite = color === COLORS.WHITE;
    const isMonochromaticButton = MONOCHROME_BUTTONS.includes(color);

    const mainClasses = classnames(
        BASE_CLASSES,
        `fit-button-color-${color}`,
        `fit-button-fill-${fill}`,
        `fit-button-size-${size}`,
        `co-${buttonTextColor({
            color,
            textColor,
            disabled,
            isGhost,
            isWhite,
            isMonochromaticButton
        })}`,
        className,
        {
            'fit-button-loading': loading,
            'fit-button-disabled': disabled,
            [`fit-button-text-color-${textColor}`]: isWhite,
            [`bg-co-${backgroundColor({
                color,
                disabled,
                isWhite,
                isMonochromaticButton
            })}`]: !isGhost
        }
    );

    const iconClasses = classnames(
        'fit-button-loading-icon',
        `co-${iconColor({
            color,
            isGhost
        })}`
    );

    return {
        main: mainClasses,
        icon: iconClasses
    };
};

export default generateClasses;
